import arrayMove from "array-move";
import { produce } from "immer";
import React from "react";

import { doNothing } from "@examspark/common";
import { questionTypesList } from "@examspark/common/dist/meta/questions";
import {
  InteractionTypeEnum,
  Course,
  Objective,
  Resource,
  SearchableResourceFilterInput,
  Tag,
  Unit,
} from "@examspark/types";
export interface NewActivityObjective extends Objective {
  id: string;

  // if the builder form you are using can query an objective multiple times,
  // provide a uniqueId to help identify the objective for toggling.
  uniqueId?: string;

  enabled?: boolean;
  abbreviation: string;
  sequence: number;
  content: string;
  qty: string;
  tags?: Tag[];

  additionalFilter?: SearchableResourceFilterInput;
}

export interface NewActivityState {
  name: string;
  description: string;
  isInitialized?: boolean;

  // todo: refactor these somehow to be specific to the bonfire app
  banks?: string[];
  subject?: string;
  units?: Unit[];
  courses?: Course[];

  type?: string;
  grade?: string;
  difficulty?: string;
  maturity?: string;
  language?: string;
  document?: string | null;
  /** @deprecated use `sections` */
  section?: string;
  sections?: string[];
  objectives: NewActivityObjective[];
  passages: Resource[];
  passagesAvailableCount: number;
  interactionTypes: string[];
  questions: Resource[];
  questionQty?: number;
  isIncludeAllPassageItemsEnabled?: boolean;
  isIncludeOnlyPassageItemsEnabled?: boolean;
  objectiveAuthorityIds?: string[];
  config: {
    allowRetakes?: boolean;
    allowFeedback?: boolean;
  };
}

export const itemTypeLabelOverrides: {
  [key: string]: string;
} = {
  [InteractionTypeEnum.TextExtended]: "Essay",
  [InteractionTypeEnum.TextSelectable]: "HotText",
  [InteractionTypeEnum.TextGapMatch]: "Drag & Drop (Text)",
  [InteractionTypeEnum.GraphicHotspot]: "Hotspot",
  [InteractionTypeEnum.TextInlineChoice]: "Inline Choice",
  [InteractionTypeEnum.TextInlineEntry]: "Text Entry",
  [InteractionTypeEnum.TextInlineMathEntry]: "Equation Entry",
  [InteractionTypeEnum.TextInlineNumericEntry]: "Numeric Entry",
  [InteractionTypeEnum.Order]: "Sorting/Ordering",
  [InteractionTypeEnum.GraphicGapMatch]: "Drag & Drop (Graphic)",
  [InteractionTypeEnum.Audio]: "Audio Response",
};

export const betaItemTypes: InteractionTypeEnum[] = [
  InteractionTypeEnum.Audio,
  InteractionTypeEnum.Gridded,
  // InteractionTypeEnum.BarChart,
  // InteractionTypeEnum.CartesianGraph,
  // InteractionTypeEnum.GraphicGapMatch,
  // InteractionTypeEnum.GraphicHotspot,
  // InteractionTypeEnum.LineChart,
  // InteractionTypeEnum.NumberLinePlot,
];

export const defaultNewActivityState: NewActivityState = {
  config: {},
  description: "",
  document: null,
  interactionTypes: questionTypesList,
  isIncludeAllPassageItemsEnabled: true,
  isIncludeOnlyPassageItemsEnabled: true,
  name: "",
  objectives: [],
  passages: [],
  passagesAvailableCount: 0,
  questionQty: 10,
  questions: [],
  sections: [],
};

export const sampleNewActivityState: NewActivityState = {
  name: "Sample Exam",
  description: "This is the description",
  questions: [],
  config: {},
  interactionTypes: ["choice"],
  passages: [],
  passagesAvailableCount: 0,
  isIncludeAllPassageItemsEnabled: true,
  objectives: [
    {
      content:
        "use a problem-solving model that incorporates analyzing given information, formulating a plan or strategy, determining a solution, justifying the solution, and evaluating the problem-solving process and the reasonableness of the solution;",
      abbreviation: "6.1.B",
      sequence: 3,
      id: "828b94e0-0520-505a-8983-6f45611827cc",
      sk: "standard",
      type: "OBJECTIVE",
      qty: "2",
    },
    {
      content:
        "communicate mathematical ideas, reasoning, and their implications using multiple representations, including symbols, diagrams, graphs, and language as appropriate;",
      abbreviation: "6.1.D",
      sequence: 5,
      id: "a74a97f4-2960-57f3-ab89-96472c266118",
      sk: "standard",
      type: "OBJECTIVE",
      qty: "7",
    },
  ],
  type: "HOMEWORK",
  document: "document#tea#teks#ma:2012",
  section: "section#tea#teks#ma:2012#6",
  language: "EN",
};

export type NewActivityActions =
  | {
      type: "UPDATE";
      data: NewActivityState;
    }
  | {
      type: "PRODUCE_UPDATE";
      producer: (draft: NewActivityState) => void;
    }
  | {
      type: "SET_BANKS";
      data: string[];
    }
  | {
      type: "TOGGLE_OBJECTIVE";
      objective: NewActivityObjective;
      enabled?: boolean;
    }
  | {
      type: "TOGGLE_OBJECTIVES";
      enabled: boolean;
    }
  | {
      type: "TOGGLE_ALL_PASSAGE_ITEMS";
      enabled?: boolean;
    }
  | {
      type: "TOGGLE_ONLY_PASSAGE_ITEMS";
      enabled?: boolean;
    }
  | {
      type: "SET_OBJECTIVE_QTY";
      objective: NewActivityObjective;
      qty: string;
    }
  | {
      type: "REMOVE_ITEM_AT_INDEX";
      index: number;
    }
  | {
      type: "REMOVE_ITEMS_AT_INDEX";
      indexes: number[];
    }
  | {
      type: "SET_PASSAGES_AVAILABLE_COUNT";
      count: number;
    }
  | {
      type: "ADD_ITEMS";
      questions: Resource[];
      index?: number;
    }
  | {
      type: "REPLACE_ITEM_AT_INDEX";
      index: number;
      questions: Resource[];
    }
  | {
      type: "REORDER_ITEMS";
      oldIndex: number;
      newIndex: number;
    }
  | {
      type: "RESET";
    };

export const dispatch = doNothing;

/**
 * Context for the guided activity builder. Caution not to mistake this for the
 * ExamEditorContext whose reducer is mostly similar to this one, but has key
 * differences that make it not suitable for the guided exam builder..
 * */
export const NewActivityContext = React.createContext<
  [NewActivityState, React.Dispatch<NewActivityActions>]
>([defaultNewActivityState, dispatch]);

export const newActivityReducer = (
  state: NewActivityState,
  action: NewActivityActions,
) => {
  // console.log(action);
  switch (action.type) {
    case "UPDATE":
      return action.data;
    case "PRODUCE_UPDATE":
      return produce(state, action.producer);
    case "RESET":
      return { ...defaultNewActivityState };
    case "ADD_ITEMS":
      for (const item of action.questions) {
        if (!state.questions.find((q) => q.id === item.id)) {
          if (action.index == null) {
            state.questions.push(item);
          } else {
            state.questions.splice(action.index, 0, item);
          }
        }
      }
      break;
    case "TOGGLE_ALL_PASSAGE_ITEMS":
      state.isIncludeAllPassageItemsEnabled =
        action.enabled !== undefined
          ? action.enabled
          : !state.isIncludeAllPassageItemsEnabled;
      break;
    case "TOGGLE_ONLY_PASSAGE_ITEMS":
      state.isIncludeOnlyPassageItemsEnabled =
        action.enabled !== undefined
          ? action.enabled
          : !state.isIncludeOnlyPassageItemsEnabled;
      break;
    case "REPLACE_ITEM_AT_INDEX":
      state.questions.splice(action.index, 1, ...action.questions);
      break;
    case "REORDER_ITEMS":
      state.questions = arrayMove(
        state.questions,
        action.oldIndex,
        action.newIndex,
      );
      break;
    case "TOGGLE_OBJECTIVE": {
      const index = state.objectives.findIndex(
        (o) =>
          (o.uniqueId ?? o.id) ===
          (action.objective.uniqueId ?? action.objective.id),
      );
      if (index !== -1) {
        state.objectives[index].enabled =
          action.enabled ?? !state.objectives[index].enabled;
      }
      break;
    }
    case "TOGGLE_OBJECTIVES":
      for (const objective of state.objectives) {
        objective.enabled = action.enabled;
      }
      break;
    case "REMOVE_ITEM_AT_INDEX":
      state.questions.splice(action.index, 1);
      break;
    case "REMOVE_ITEMS_AT_INDEX": {
      const next: Resource[] = [];
      for (let i = 0; i < state.questions.length; i++) {
        if (!action.indexes.includes(i)) {
          next.push(state.questions[i]);
        }
      }

      state.questions = next;
      break;
    }
    case "SET_BANKS":
      state.banks = action.data;
      break;
    case "SET_PASSAGES_AVAILABLE_COUNT":
      state.passagesAvailableCount = action.count;
      break;
  }
};
